import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CenterContainer } from '@xing-com/platform-layout-view-wrapper';
import { Option } from '@xing-com/text-field';
import { Headline } from '@xing-com/typography';

import { SimpleCompanyCardGrid } from '../../../../components/simple-company-card-grid/simple-company-card-grid';
import { useBestCompaniesByCity } from '../../../../hooks/use-best-companies-by-city/use-best-companies-by-city';
import { useCities } from '../../../../hooks/use-cities/use-cities';
import { useScreen } from '../../../../hooks/use-screen/use-screen';
import { createSimpleCompanies } from '../../../../models/simple-company';
import * as Styled from './best-companies-by-city.styled';

// This should come from a prev request
export const availableCities = {
  Berlin: '2950159.e2912c',
  Hamburg: '2911298.ce939c',
  Munich: '2867714.724c7f',
  Cologne: '2886242.91e981',
  Frankfurt: '2925533.36f095',
  Stuttgart: '2825297.22f76d',
  Düsseldorf: '2934246.61a5d4',
  Dortmund: '2879139.664199',
  Essen: '2928810.1c24e9',
  Leipzig: '2935517.8cce46',
};
export const availableCitiesIds = Object.values(availableCities);

const LIMIT_MOBILE = 12;
const LIMIT_WIDE = 18;

export const BestCompaniesByCity: FC = () => {
  const { formatMessage } = useIntl();
  const { isSmallScreen } = useScreen();
  const [citySelected, setCitySelected] = useState<string>(
    availableCitiesIds[0]
  );

  const LIMIT = isSmallScreen ? LIMIT_MOBILE : LIMIT_WIDE;

  const { data: citiesData, loading: citiesLoading } =
    useCities(availableCitiesIds);

  const citiesOptions = citiesData?.cities ?? [];

  const { data: bestCompaniesByCityData, loading: bestCompaniesByCityLoading } =
    useBestCompaniesByCity({
      city: citySelected,
      limit: LIMIT,
    });

  const bestCompaniesByCity =
    bestCompaniesByCityData?.topCompaniesByCity?.collection ?? [];

  const simpleCompanyCards = useMemo(
    // @ts-expect-error ignore null errors from graphql
    () => createSimpleCompanies(bestCompaniesByCity),
    [bestCompaniesByCity]
  );

  const loadingSection = citiesLoading || bestCompaniesByCityLoading;

  return (
    <CenterContainer>
      <Styled.SectionWrapper>
        <Styled.HeaderContainer>
          <Headline noMargin size="xxlarge" sizeConfined="xxxlarge">
            {formatMessage({ id: 'CP_BEST_COMPANIES_BY_CITY_TITLE' })}
          </Headline>
          <Styled.Dropdown
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setCitySelected(e.target.value)
            }
            placeholder={citySelected ? '' : 'City'}
            size="medium"
            value={citySelected}
          >
            {citiesOptions.map(
              (city) =>
                city && (
                  <Option key={city.name} value={city.id}>
                    {city.name}
                  </Option>
                )
            )}
          </Styled.Dropdown>
        </Styled.HeaderContainer>
        {loadingSection ? (
          <SimpleCompanyCardGrid.Skeleton amountOfCards={LIMIT} />
        ) : (
          <SimpleCompanyCardGrid items={simpleCompanyCards} />
        )}
      </Styled.SectionWrapper>
    </CenterContainer>
  );
};
